import { Controller } from '@hotwired/stimulus'

export default class GoogleMapController extends Controller {
  static targets = ['myMap', 'radius', 'showradius', 'address']

  connect() {
    this.formPrefix = this.element.dataset.googleMapFormPrefix

    if (this.hasMyMapTarget) {
      const initialPosition = {
        lat: Number.parseFloat(this.myMapTarget.dataset.lat) || 40.7128,
        lng: Number.parseFloat(this.myMapTarget.dataset.lng) || -74.006,
        city: this.myMapTarget.dataset.city || '',
        state: this.myMapTarget.dataset.state || '',
        country: this.myMapTarget.dataset.country || '',
        place: this.myMapTarget.dataset.place || ''
      }

      this.setFormFields(initialPosition)
      this.initMap(initialPosition.lat, initialPosition.lng)

      this.updateFormFields({
        lat: initialPosition.lat,
        lng: initialPosition.lng
      })
    }
  }

  setFormFields(fields) {
    for (const [key, value] of Object.entries(fields)) {
      if (!this.formPrefix) return
      const input = document.getElementById(`${this.formPrefix}_${key}`)
      if (input) input.value = value || ''
    }

    if (this.hasAddressTarget) {
      this.addressTarget.value = fields.place || ''
    }

    const otherController =
      this.application.getControllerForElementAndIdentifier(
        document.getElementById('new-audience-form'),
        'audience'
      )
    otherController?.setLocalLocationFormData(fields)
  }

  async initMap(lat, lng) {
    const position = { lat: lat, lng: lng }
    const mapId = this.myMapTarget

    const { Map } = await google.maps.importLibrary('maps')
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')

    this.map = new Map(mapId, {
      center: position,
      zoom: 10,
      mapId: this.myMapTarget.dataset.key
    })

    this.marker = new AdvancedMarkerElement({
      map: this.map,
      position: position,
      title: ''
    })

    this.circle = new google.maps.Circle({
      map: this.map,
      center: position,
      radius: Number.parseFloat(this.radiusTarget.value) * 1000,
      strokeColor: '#1E90FF',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#1E90FF',
      fillOpacity: 0.35
    })

    this.addMapClickListener()
  }

  addMapClickListener() {
    this.map.addListener('click', (event) => {
      const clickedLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
      this.updateMarkerPosition(clickedLocation)
      this.updateFormFields(clickedLocation)
    })
  }

  updateMarkerPosition(position) {
    this.marker.position = position
    this.circle.setCenter(position)
    this.map.setCenter(position)
    this.updateFormFields(position)
  }

  async updateFormFields(position) {
    const geocoder = new google.maps.Geocoder()
    const location = await this.reverseGeocode(geocoder, position)

    const fields = {
      lat: position.lat,
      lng: position.lng,
      city: location.city,
      state: location.state,
      country: location.country,
      place: location.place
    }

    this.setFormFields(fields)
  }

  async reverseGeocode(geocoder, position) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const addressComponents = results[0].address_components

          const city =
            addressComponents.find((component) =>
              component.types.includes('locality')
            )?.long_name || ''
          const country =
            addressComponents.find((component) =>
              component.types.includes('country')
            )?.long_name || ''
          const state =
            addressComponents.find((component) =>
              component.types.includes('administrative_area_level_1')
            )?.long_name || ''

          resolve({
            city: city,
            country: country,
            state: state,
            place: results[0].formatted_address
          })
        } else {
          reject(new Error('Geocoding failed: ' + status))
        }
      })
    })
  }

  flyToLocation(lat, lng) {
    const newPosition = {
      lat: Number.parseFloat(lat),
      lng: Number.parseFloat(lng)
    }
    this.updateMarkerPosition(newPosition)
  }

  updateRadius(event) {
    const radiusInKm = Number.parseFloat(event.target.value)
    if (radiusInKm > 100) {
      const radiusInMeters = 100 * 1000
      this.showradiusTarget.innerHTML = `100 KMS`
      this.circle.setRadius(radiusInMeters)
      this.showAlert('Error', 'Max Radius is 100', 'error')
      event.target.setAttribute('max', 100)
      event.target.value = 100
      return
    }
    const radiusInMeters = radiusInKm * 1000
    this.showradiusTarget.innerHTML = `${radiusInKm} KMS`
    this.circle.setRadius(radiusInMeters)
  }

  showAlert(title, message, icon) {
    Swal.fire({
      customClass: {
        popup:
          'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        htmlContainer: 'text-black-paint text-white'
      },
      animation: false,
      title: title,
      text: message,
      icon: icon,
      confirmButtonText: 'OK'
    })
  }
}
